import React from 'react';
import { Box, Typography, Tooltip as MuiTooltip } from '@mui/material';
import { Table, Tag } from 'antd';
import { useTranslation } from 'react-i18next';
import { InfoCircleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { colors } from '../../themes/typography';
import { responsiveTypography } from '../../themes/responsive';
import 'antd/dist/reset.css';

// Styles for table customization
const styles = `
  .product-problems-table .ant-table-cell {
    padding: 8px 12px !important;
    font-size: 13px !important;
  }
  
  .product-problems-table .ant-table-thead > tr > th {
    background-color: #fafafa !important;
    font-weight: 600 !important;
    color: #262626 !important;
    border-bottom: 2px solid #f0f0f0 !important;
  }
  
  .product-problems-table .ant-table-tbody > tr:hover > td {
    background-color: #f5f5f5 !important;
  }
  
`;

// Add style tag to document
if (typeof document !== 'undefined') {
  const existingStyle = document.getElementById('product-problems-table-styles');
  if (!existingStyle) {
    const styleTag = document.createElement('style');
    styleTag.id = 'product-problems-table-styles';
    styleTag.innerHTML = styles;
    document.head.appendChild(styleTag);
  }
}

// Helper function for monthly data rendering
const renderMonthlyValue = (value) => {
  return (
    <Typography sx={{
      fontSize: '13px',
      fontWeight: 600,
      color: colors.text.primary,
      textAlign: 'center'
    }}>
      {value}
    </Typography>
  );
};

// Helper function for group rendering with consistent formatting
const renderGroup = (group) => {
  const formattedGroup = group.charAt(0).toUpperCase() + group.slice(1);
  return (
    <Typography sx={{
      fontSize: '13px',
      fontWeight: 500,
      color: colors.text.primary
    }}>
      {formattedGroup}
    </Typography>
  );
};

// Helper function for clickable store ID rendering  
const renderStoreId = (storeId, record, navigate) => {
  const handleStoreIdClick = () => {
    if (record.originalData.group && record.originalData.location) {
      navigate('/machines', {
        state: {
          defaultTab: 'planograma',
          filters: {
            group: record.originalData.group,
            location: record.originalData.location
          }
        }
      });
    }
  };

  return (
    <MuiTooltip title={`${record.originalData.group} - ${record.originalData.location}`} arrow placement="top">
      <Typography
        component="span"
        onClick={handleStoreIdClick}
        sx={{
          color: '#6b7280', // More subtle blue-gray
          textDecoration: 'underline',
          cursor: 'pointer',
          fontSize: '13px',
          fontWeight: 600,
          fontFamily: 'monospace',
          '&:hover': {
            color: colors.action.primary,
            textDecoration: 'underline'
          }
        }}
      >
        {storeId}
      </Typography>
    </MuiTooltip>
  );
};

const ProductProblemsTable = ({ 
  data = [], 
  loading = false 
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Generate month labels (current month going back 3 months)
  const getMonthLabels = () => {
    const currentDate = new Date();
    const months = [];
    const monthNames = [
      t('january'), t('february'), t('march'), t('april'), t('may'), t('june'),
      t('july'), t('august'), t('september'), t('october'), t('november'), t('december')
    ];
    
    for (let i = 0; i < 4; i++) {
      const monthDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
      const monthIndex = monthDate.getMonth();
      const monthName = monthNames[monthIndex].slice(0, 3); // Get first 3 characters for abbreviation
      const year = monthDate.getFullYear().toString().slice(-2);
      months.push(`${monthName}-${year}`);
    }
    
    return {
      'M': months[0],
      'M-1': months[1], 
      'M-2': months[2],
      'M-3': months[3]
    };
  };

  const monthLabels = getMonthLabels();

  // Transform data for table consumption
  const tableData = data.map((problem, index) => ({
    key: problem.id || index,
    id: problem.id,
    group: problem.group,
    machineId: problem.machineId,
    storeId: problem.store_id,
    location: problem.location,
    monthM: problem.monthlyData?.['M'] || 0,
    monthM1: problem.monthlyData?.['M-1'] || 0,
    monthM2: problem.monthlyData?.['M-2'] || 0,
    monthM3: problem.monthlyData?.['M-3'] || 0,
    problemType: problem.problemType,
    experiment: problem.experiment,
    originalData: problem
  }));

  // Table columns definition
  const columns = [
    {
      title: (
        <Typography sx={{ fontSize: '13px', fontWeight: 600, color: colors.text.primary }}>
          {t('group')}
        </Typography>
      ),
      dataIndex: 'group',
      key: 'group',
      width: 100,
      render: renderGroup,
    },
    {
      title: (
        <Typography sx={{ fontSize: '13px', fontWeight: 600, color: colors.text.primary }}>
          {t('machineId')}
        </Typography>
      ),
      dataIndex: 'storeId',
      key: 'storeId',
      width: 120,
      render: (storeId, record) => renderStoreId(storeId, record, navigate),
    },
    {
      title: (
        <Typography sx={{ fontSize: '13px', fontWeight: 600, color: colors.text.primary }}>
          {monthLabels['M-3']}
        </Typography>
      ),
      dataIndex: 'monthM3',
      key: 'monthM3',
      width: 70,
      align: 'center',
      render: renderMonthlyValue,
    },
    {
      title: (
        <Typography sx={{ fontSize: '13px', fontWeight: 600, color: colors.text.primary }}>
          {monthLabels['M-2']}
        </Typography>
      ),
      dataIndex: 'monthM2',
      key: 'monthM2',
      width: 70,
      align: 'center',
      render: renderMonthlyValue,
    },
    {
      title: (
        <Typography sx={{ fontSize: '13px', fontWeight: 600, color: colors.text.primary }}>
          {monthLabels['M-1']}
        </Typography>
      ),
      dataIndex: 'monthM1',
      key: 'monthM1',
      width: 70,
      align: 'center',
      render: renderMonthlyValue,
    }
  ];

  return (
    <Box sx={{
      backgroundColor: colors.background.surface,
      border: `1px solid ${colors.border.light}`,
      borderRadius: '12px',
      overflow: 'hidden'
    }}>
      {/* Table Header */}
      <Box sx={{ 
        p: 2, 
        borderBottom: `1px solid ${colors.border.light}`,
        backgroundColor: '#fafafa'
      }}>
        <Typography sx={{
          ...responsiveTypography.h3,
          color: colors.text.primary,
          mb: '4px'
        }}>
          {t('productProblems')}
        </Typography>
        <Typography sx={{
          ...responsiveTypography.caption,
          color: colors.text.secondary
        }}>
          {t('problemsFoundCount', { count: data.length })}
        </Typography>
      </Box>

      {/* Table */}
      <Table
        className="product-problems-table"
        dataSource={tableData}
        columns={columns}
        pagination={{
          pageSize: 10,
          showSizeChanger: false,
          showQuickJumper: false,
          showTotal: (total, range) => 
            `${range[0]}-${range[1]} of ${total} problems`,
          position: ['bottomCenter']
        }}
        loading={loading}
        size="small"
        locale={{
          emptyText: (
            <Box sx={{ py: 4 }}>
              <Typography sx={{ 
                color: colors.text.secondary,
                ...responsiveTypography.body 
              }}>
                {t('noProblemsFound')}
              </Typography>
            </Box>
          )
        }}
        scroll={{ x: 'max-content' }}
      />
    </Box>
  );
};

export default ProductProblemsTable;
